import { Injectable } from "@angular/core";
import { Router } from "@angular/router";
import { Subject } from "rxjs";
import {
  confirmSignIn,
  fetchAuthSession,
  getCurrentUser,
  signIn,
  signOut,
  type SignInOutput,
} from "aws-amplify/auth";
import { AuthService } from "./auth.service";

export enum AuthFlowStatus {
  Authenticated = "AUTHENTICATED",
  EmailOtpRequired = "EMAIL_OTP_REQUIRED",
  NewPasswordRequired = "NEW_PASSWORD_REQUIRED",
  UnsupportedChallenge = "UNSUPPORTED_CHALLENGE",
  Error = "ERROR",
}

export interface AuthFlowResult {
  status: AuthFlowStatus;
  message?: string;
  nextStep?: string;
  restartRequired?: boolean;
}

@Injectable({
  providedIn: "root",
})
export class CognitoService {
  private readonly authFlowSubject = new Subject<AuthFlowResult>();
  authFlow$ = this.authFlowSubject.asObservable();

  private pendingAuthResult?: AuthFlowResult;

  constructor(private router: Router, public authService: AuthService) {}

  async login(emailaddress: string, password: string): Promise<AuthFlowResult> {
    try {
      const output = await signIn({
        username: emailaddress,
        password,
      });

      return this.handleSignInOutput(output);
    } catch (error) {
      return this.authError(error, "login");
    }
  }

  async confirmEmailOtp(code: string): Promise<AuthFlowResult> {
    try {
      const output = await confirmSignIn({
        challengeResponse: code,
      });

      return this.handleSignInOutput(output);
    } catch (error) {
      return this.authError(error, "otp");
    }
  }

  async changePassword(
    oldPassword: string,
    newPassword: string,
    confirmPassword: string
  ): Promise<AuthFlowResult> {
    if (newPassword !== confirmPassword) {
      return {
        status: AuthFlowStatus.Error,
        message: "Las contrasenas no coinciden",
      };
    }

    if (!this.hasPendingNewPasswordChallenge()) {
      return {
        status: AuthFlowStatus.Error,
        message: "La sesion de verificacion ya no es valida. Inicia sesion nuevamente.",
        restartRequired: true,
      };
    }

    try {
      const output = await confirmSignIn({
        challengeResponse: newPassword,
      });
      this.pendingAuthResult = undefined;
      const result = await this.handleSignInOutput(output);

      if (result.status === AuthFlowStatus.EmailOtpRequired) {
        this.pendingAuthResult = result;
        this.authFlowSubject.next(result);
        await this.router.navigate(["/login"]);
      }

      return result;
    } catch (error) {
      return this.authError(error, "newPassword");
    }
  }

  hasPendingNewPasswordChallenge(): boolean {
    return this.pendingAuthResult?.status === AuthFlowStatus.NewPasswordRequired;
  }

  consumePendingAuthResult(): AuthFlowResult | undefined {
    const result = this.pendingAuthResult;
    this.pendingAuthResult = undefined;
    return result;
  }

  async logOut(): Promise<void> {
    try {
      await signOut();
    } catch {
      // Local cleanup must still run when Cognito has already expired the session.
    } finally {
      this.authService.logout();
      await this.router.navigate(["/login"]);
    }
  }

  private async handleSignInOutput(output: SignInOutput): Promise<AuthFlowResult> {
    const signInStep = output.nextStep.signInStep;

    switch (signInStep) {
      case "DONE":
        return this.completeAuthenticatedSession();
      case "CONFIRM_SIGN_IN_WITH_EMAIL_CODE":
        return {
          status: AuthFlowStatus.EmailOtpRequired,
          nextStep: signInStep,
        };
      case "CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED":
        const result = {
          status: AuthFlowStatus.NewPasswordRequired,
          nextStep: signInStep,
        };
        this.pendingAuthResult = result;
        await this.router.navigate(["/newPasswordRequire"]);
        return result;
      default:
        return this.unsupportedChallenge(signInStep);
    }
  }

  private async completeAuthenticatedSession(): Promise<AuthFlowResult> {
    try {
      this.pendingAuthResult = undefined;
      const session = await fetchAuthSession();
      const hasValidTokens = Boolean(session.tokens?.accessToken && session.tokens?.idToken);

      if (!hasValidTokens) {
        this.authService.logout();
        return {
          status: AuthFlowStatus.Error,
          message: "La sesion de autenticacion no es valida. Inicia sesion nuevamente.",
          restartRequired: true,
        };
      }

      try {
        await getCurrentUser();
      } catch {
        // fetchAuthSession tokens are the source of truth; current user is complementary.
      }

      this.authService.login();
      await this.router.navigate(["/home"]);
      return { status: AuthFlowStatus.Authenticated };
    } catch (error) {
      return this.authError(error, "session");
    }
  }

  private async unsupportedChallenge(signInStep: string): Promise<AuthFlowResult> {
    await this.resetPendingSignIn();
    return {
      status: AuthFlowStatus.UnsupportedChallenge,
      nextStep: signInStep,
      message: "Este metodo de autenticacion no esta disponible. Contacta al administrador.",
      restartRequired: true,
    };
  }

  private async authError(error: unknown, context: "login" | "otp" | "newPassword" | "session"): Promise<AuthFlowResult> {
    const errorName = this.getErrorName(error);
    const result: AuthFlowResult = {
      status: AuthFlowStatus.Error,
      message: this.mapErrorMessage(errorName, context),
      restartRequired: this.shouldRestart(errorName, context),
    };

    if (result.restartRequired) {
      await this.resetPendingSignIn();
    }

    return result;
  }

  private async resetPendingSignIn(): Promise<void> {
    this.pendingAuthResult = undefined;
    try {
      await signOut();
    } catch {
      // Best-effort cleanup for unsupported or expired challenge state.
    } finally {
      this.authService.logout();
    }
  }

  private getErrorName(error: unknown): string {
    if (typeof error === "object" && error !== null) {
      const candidate = error as { name?: string; code?: string; __type?: string };
      return candidate.name || candidate.code || candidate.__type || "";
    }
    return "";
  }

  private mapErrorMessage(errorName: string, context: "login" | "otp" | "newPassword" | "session"): string {
    switch (errorName) {
      case "CodeMismatchException":
        return "Codigo incorrecto. Intentalo nuevamente.";
      case "ExpiredCodeException":
        return "El codigo expiro. Inicia sesion otra vez para recibir uno nuevo.";
      case "NotAuthorizedException":
        return context === "otp"
          ? "La sesion de verificacion ya no es valida. Inicia sesion nuevamente."
          : "Usuario o contrasena incorrectos.";
      case "UserNotFoundException":
        return "Usuario o contrasena incorrectos.";
      case "TooManyRequestsException":
      case "LimitExceededException":
        return "Se realizaron demasiados intentos. Espera un momento.";
      default:
        return context === "login"
          ? "Usuario o contrasena incorrectos."
          : "No se pudo completar la autenticacion. Intenta nuevamente.";
    }
  }

  private shouldRestart(errorName: string, context: "login" | "otp" | "newPassword" | "session"): boolean {
    return (
      context === "session" ||
      errorName === "ExpiredCodeException" ||
      (context === "otp" && errorName === "NotAuthorizedException")
    );
  }
}
